import React, { useState, useEffect } from 'react';
import { Icon } from '../components/Layout';
import MusicAPI from '../services/musicApi';
import Footer from '../components/Footer';
import { useMusicPlayer } from '../context/MusicPlayerContext';

export default function LikedSongs() {
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { playSong, currentSong, isPlaying } = useMusicPlayer();

  // Fetch liked songs (using featured playlists as liked songs for demo)
  useEffect(() => {
    const fetchLikedSongs = async () => {
      try {
        setIsLoading(true);

        // Get featured playlists and use them as liked songs
        const data = await MusicAPI.getFeaturedPlaylists();
        console.log("Featured Playlists for Liked Songs:", data);

        if (data?.playlists?.items?.[0]?.id) {
          const tracksData = await MusicAPI.getPlaylistTracks(data.playlists.items[0].id);

          // Format the tracks and mark them as liked
          const formattedSongs = tracksData.items.map((item, index) => ({
            id: item.track.id || index,
            title: item.track.name,
            artist: item.track.artists.map((a) => a.name).join(", "),
            album: item.track.album.name,
            cover: item.track.album.images[0]?.url || "/default-cover.jpg",
            audio: item.track.preview_url,
            isLiked: true, // Mark as liked
          }));

          setSongs(formattedSongs);
        }
      } catch (err) {
        console.error("Error fetching liked songs:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLikedSongs();
  }, []);

  const handleSongClick = (song) => {
    if (!song.audio) {
      alert("⚠️ This track has no preview available.");
      return;
    }

    // Format song data to match expected structure
    const formattedSong = {
      id: song.id,
      title: song.title,
      artist: song.artist,
      album: song.album,
      cover: song.cover,
      audioUrl: song.audio,
      duration: '3:20',
    };

    // Use the songs list as playlist
    const formattedList = songs.map(s => ({
      id: s.id,
      title: s.title,
      artist: s.artist,
      album: s.album,
      cover: s.cover,
      audioUrl: s.audio,
      duration: '3:20',
    }));

    // Play the song using centralized music player
    playSong(formattedSong, formattedList);
  };

  const removeFromLiked = (songId) => {
    setSongs(songs.filter(song => song.id !== songId));
  };

  const handlePlayAll = () => {
    if (songs.length > 0) {
      handleSongClick(songs[0]);
    }
  };

  return (
    <>

      <div className="space-y-4 xs:space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="flex flex-col xs:flex-row items-center xs:items-start gap-3 xs:gap-4 sm:gap-6 text-center xs:text-left">
          <div className="w-32 h-32 xs:w-40 xs:h-40 sm:w-48 sm:h-48 bg-gradient-to-br from-fuchsia-500/20 to-pink-500/20 rounded-xl xs:rounded-2xl border border-white/10 flex items-center justify-center flex-shrink-0">
            <Icon.Heart className="w-12 h-12 xs:w-16 xs:h-16 sm:w-20 sm:h-20 text-pink-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs xs:text-sm text-slate-400 mb-1 xs:mb-2">Playlist</p>
            <h1 className="text-2xl xs:text-3xl sm:text-4xl font-bold mb-2 xs:mb-4">Liked Songs</h1>
            <p className="text-sm xs:text-base text-slate-400">
              {songs.length} song{songs.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center xs:justify-start gap-2 xs:gap-3 sm:gap-4">
          <button
            onClick={handlePlayAll}
            disabled={songs.length === 0}
            className="grid place-items-center h-12 w-12 rounded-full bg-fuchsia-600 text-white shadow hover:bg-fuchsia-700 transition disabled:bg-slate-600"
          >
            <Icon.Play className="w-6 h-6" />
          </button>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fuchsia-500"></div>
          </div>
        ) : (
          /* Songs List */
          <div className="bg-white/5 rounded-xl xs:rounded-2xl border border-white/10 overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 text-left text-slate-400 text-sm">
                    <th className="p-3 xs:p-4 w-12">#</th>
                    <th className="p-3 xs:p-4">Title</th>
                    <th className="p-3 xs:p-4">Album</th>
                    <th className="p-3 xs:p-4 text-right">Duration</th>
                    <th className="p-3 xs:p-4 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {songs.map((song, index) => (
                    <tr
                      key={song.id}
                      className={`border-b border-white/5 hover:bg-white/10 cursor-pointer ${currentSong?.id === song.id ? 'bg-white/10' : ''}`}
                      onClick={() => handleSongClick(song)}
                    >
                      <td className="p-3 xs:p-4 w-12">
                        {currentSong?.id === song.id && isPlaying ? (
                          <Icon.Volume className="w-4 h-4 text-fuchsia-500" />
                        ) : (
                          <span className="text-slate-400">{index + 1}</span>
                        )}
                      </td>
                      <td className="p-3 xs:p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={song.cover}
                            alt={song.title}
                            className="h-10 w-10 rounded object-cover"
                          />
                          <div>
                            <div className={`font-medium ${currentSong?.id === song.id ? 'text-fuchsia-500' : 'text-white'}`}>
                              {song.title}
                            </div>
                            <div className="text-sm text-slate-400">{song.artist}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 xs:p-4 text-slate-400 text-sm">
                        {song.album}
                      </td>
                      <td className="p-3 xs:p-4 text-right text-slate-400 text-sm">
                        3:20 {/* Mock duration */}
                      </td>
                      <td className="p-3 xs:p-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromLiked(song.id);
                          }}
                          className="p-1 rounded-full hover:bg-white/10 transition"
                        >
                          <Icon.Heart className="w-4 h-4 text-pink-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-2 p-2 xs:p-3">
              {songs.map((song, index) => (
                <div
                  key={song.id}
                  className={`bg-white/5 rounded-lg xs:rounded-xl p-3 xs:p-4 hover:bg-white/10 cursor-pointer transition-all ${currentSong?.id === song.id ? 'bg-white/10' : ''}`}
                  onClick={() => handleSongClick(song)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 xs:w-10 flex-shrink-0">
                      {currentSong?.id === song.id && isPlaying ? (
                        <Icon.Volume className="w-4 h-4 xs:w-5 xs:h-5 text-fuchsia-500" />
                      ) : (
                        <span className="text-slate-400 text-sm xs:text-base">{index + 1}</span>
                      )}
                    </div>
                    <img
                      src={song.cover}
                      alt={song.title}
                      className="h-10 w-10 xs:h-12 xs:w-12 rounded object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate ${currentSong?.id === song.id ? 'text-fuchsia-500' : 'text-white'}`}>
                        {song.title}
                      </div>
                      <div className="text-xs xs:text-sm text-slate-400 truncate">{song.artist}</div>
                      <div className="text-xs text-slate-500 truncate mt-1">{song.album}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFromLiked(song.id);
                      }}
                      className="p-1 rounded-full hover:bg-white/10 transition flex-shrink-0"
                    >
                      <Icon.Heart className="w-3.5 h-3.5 xs:w-4 xs:h-4 text-pink-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {songs.length === 0 && !isLoading && (
          <div className="text-center py-12 xs:py-16 sm:py-20">
            <Icon.Heart className="w-12 h-12 xs:w-16 xs:h-16 text-slate-400 mx-auto mb-3 xs:mb-4" />
            <h3 className="text-lg xs:text-xl font-semibold mb-2">Songs you like will appear here</h3>
            <p className="text-sm xs:text-base text-slate-400">Save songs by tapping the heart icon.</p>
          </div>
        )}
      </div>

    <Footer/>
    </>

  );
}